import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import {
  Typography,
  Table,
  Button,
  Tag,
  Space,
  Modal,
  message,
  Breadcrumb,
  Switch,
  Select,
  Drawer,
  Form,
  Input,
  InputNumber,
  Radio,
  Descriptions
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ExclamationCircleOutlined,
  SearchOutlined,
  ThunderboltOutlined
} from "@ant-design/icons";
import styled from "styled-components";

import { WHITE } from "../../constant/colors";
import {
  getNudgeRulesAPI,
  createNudgeRuleAPI,
  updateNudgeRuleAPI,
  deleteNudgeRuleAPI,
  getNudgesForPatientAPI,
  getBrandJourneyConfigurationsAPI
} from "../../api/api";

const { Title, Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;

const PageContainer = styled.div`
  width: 98%;
  margin: 0 auto;
  padding: 24px;
  background: ${WHITE};
  border-radius: 12px;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
  height: calc(100vh - 100px);
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

// status: "live" = auto-detected by platform, "api" = must be pushed via external API, "planned" = not yet implemented
const EVENT_TYPES = [
  {
    value: "prior_auth_denied",
    label: "PA Denied",
    status: "live",
    hint: "Auto-detected when a patient's prior authorization is denied"
  },
  {
    value: "prior_auth_approved",
    label: "PA Approved",
    status: "live",
    hint: "Auto-detected when a patient's prior authorization is approved"
  },
  {
    value: "pa_status_changed",
    label: "PA Status Changed (Any)",
    status: "live",
    hint: "Auto-detected when a patient's prior auth status changes to any value"
  },
  {
    value: "journey_step_completed",
    label: "Journey Step Completed",
    status: "live",
    hint: "Auto-detected when a patient completes a journey milestone (wallet viewed, support interacted)"
  },
  {
    value: "insurance_changed",
    label: "Insurance Changed",
    status: "planned",
    hint: "Will auto-detect when insurance data is updated. Can also be pushed via API."
  },
  {
    value: "pa_expiration",
    label: "PA Expiration",
    status: "planned",
    hint: "Will auto-detect based on PA expiry dates. Can also be pushed via API."
  },
  {
    value: "enrollment_completed",
    label: "Enrollment Completed",
    status: "planned",
    hint: "Will auto-detect when a patient completes program enrollment."
  },
  {
    value: "document_uploaded",
    label: "Document Uploaded",
    status: "planned",
    hint: "Will auto-detect when a patient uploads a document."
  },
  {
    value: "refill_upcoming",
    label: "Refill Upcoming",
    status: "planned",
    hint: "Will auto-detect based on refill schedule."
  },
  {
    value: "patient_inactive",
    label: "Patient Inactive",
    status: "planned",
    hint: "Will auto-detect when a patient hasn't logged in for X days."
  },
  {
    value: "custom",
    label: "Custom Event",
    status: "api",
    hint: "Catch-all for any event not in this list. Always pushed via API."
  },
  {
    value: "copay_card_activated",
    label: "Copay Card Activated",
    status: "api",
    hint: "Partner pushes when a copay card is activated."
  },
  {
    value: "copay_card_expiring",
    label: "Copay Card Expiring",
    status: "api",
    hint: "Partner pushes when a copay card is nearing expiration."
  },
  {
    value: "benefit_verification_complete",
    label: "Benefit Verification Complete",
    status: "api",
    hint: "Partner pushes when benefit verification is completed."
  },
  {
    value: "prior_auth_approved",
    label: "Prior Auth Approved",
    status: "api",
    hint: "Partner pushes when an external PA is approved."
  },
  {
    value: "prior_auth_denied",
    label: "Prior Auth Denied",
    status: "api",
    hint: "Partner pushes when an external PA is denied."
  },
  {
    value: "shipment_dispatched",
    label: "Shipment Dispatched",
    status: "api",
    hint: "Partner pushes when specialty pharmacy dispatches a shipment."
  },
  {
    value: "shipment_at_risk",
    label: "Shipment at Risk",
    status: "api",
    hint: "Partner pushes when a shipment is delayed or at risk."
  },
  {
    value: "appointment_reminder",
    label: "Appointment Reminder",
    status: "api",
    hint: "Partner pushes for upcoming appointment reminders."
  },
  {
    value: "scheduling_failure",
    label: "Scheduling Failure",
    status: "api",
    hint: "Partner pushes when scheduling confirmation fails."
  },
  {
    value: "symptom_reported",
    label: "Symptom Reported",
    status: "api",
    hint: "Partner pushes when a patient reports symptom instability."
  },
  {
    value: "clinical_milestone",
    label: "Clinical Milestone",
    status: "api",
    hint: "Partner pushes for clinical milestones (e.g., treatment cycle complete)."
  },
  {
    value: "coverage_change_risk",
    label: "Coverage Change Risk",
    status: "api",
    hint: "Partner pushes predictive alerts based on historical insurance data."
  },
  {
    value: "frequent_question",
    label: "Frequent Question",
    status: "api",
    hint: "Partner pushes when a patient frequently asks about a topic (e.g., affordability)."
  }
];

const PRIORITY_OPTIONS = [
  { value: 1, label: "Critical", color: "red" },
  { value: 2, label: "High", color: "orange" },
  { value: 3, label: "Standard", color: "blue" },
  { value: 4, label: "Low", color: "default" }
];

const CHANNEL_OPTIONS = [
  { value: "in_app", label: "In-App Chat" },
  { value: "sms", label: "SMS (Phase 2)" },
  { value: "email", label: "Email (Phase 2)" }
];

const NudgeRuleConfiguration = () => {
  const [rules, setRules] = useState([]);
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(false);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const [patientSearchId, setPatientSearchId] = useState("");
  const [patientNudges, setPatientNudges] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [rulesRes, brandsRes] = await Promise.all([getNudgeRulesAPI(), getBrandJourneyConfigurationsAPI()]);
      setRules(rulesRes.data);
      setBrands(brandsRes.data);
    } catch {
      message.error("Failed to load nudge rules");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);

      const data = {
        brandJourneyConfiguration: values.brandJourneyConfiguration,
        eventType: values.eventType,
        name: values.name,
        description: values.description || null,
        channel: values.channel || "in_app",
        messageTemplate: values.messageTemplate,
        priority: values.priority || 3,
        cooldownHours: values.cooldownHours != null ? values.cooldownHours : 24,
        suppressIfActive: values.suppressIfActive || false,
        dailyCap: values.dailyCap != null ? values.dailyCap : 5,
        isEnabled: values.isEnabled !== false,
        showOnMedsPage: values.showOnMedsPage || false
      };

      if (editingItem) {
        await updateNudgeRuleAPI(editingItem.id, data);
        message.success("Rule updated");
      } else {
        await createNudgeRuleAPI(data);
        message.success("Rule created");
      }

      setDrawerVisible(false);
      setEditingItem(null);
      form.resetFields();
      fetchData();
    } catch (error) {
      if (!error.errorFields) message.error("Failed to save rule");
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (record) => {
    setEditingItem(record);
    form.setFieldsValue({ ...record });
    setDrawerVisible(true);
  };

  const handleDelete = (record) => {
    Modal.confirm({
      title: `Delete rule "${record.name}"?`,
      icon: <ExclamationCircleOutlined />,
      okText: "Delete",
      okType: "danger",
      onOk: async () => {
        await deleteNudgeRuleAPI(record.id);
        message.success("Rule deleted");
        fetchData();
      }
    });
  };

  const handlePatientSearch = async () => {
    if (!patientSearchId) return;
    try {
      const { data } = await getNudgesForPatientAPI(patientSearchId);
      setPatientNudges(data);
    } catch {
      message.error("Failed to fetch patient nudges");
      setPatientNudges(null);
    }
  };

  const handleOpenDrawer = () => {
    setEditingItem(null);
    form.resetFields();
    setDrawerVisible(true);
  };

  const columns = [
    { title: "Name", dataIndex: "name", key: "name", width: 180 },
    {
      title: "Event Type",
      dataIndex: "eventType",
      key: "eventType",
      width: 180,
      render: (t) => {
        const et = EVENT_TYPES.find((e) => e.value === t);
        const statusColors = { live: "green", api: "purple", planned: "default" };
        const statusLabels = { live: "Auto", api: "API", planned: "Planned" };
        return (
          <Space size={4}>
            <Tag color={statusColors[et?.status] || "default"} style={{ fontSize: 10, padding: "0 4px" }}>
              {statusLabels[et?.status] || "?"}
            </Tag>
            <span>{et?.label || t}</span>
          </Space>
        );
      }
    },
    {
      title: "Brand",
      dataIndex: "brandJourneyConfiguration",
      key: "brand",
      width: 120,
      render: (id) => brands.find((b) => b.id === id)?.brandName || "-"
    },
    {
      title: "Channel",
      dataIndex: "channel",
      key: "channel",
      width: 100,
      render: (ch) => {
        const colors = { in_app: "green", sms: "blue", email: "orange" };
        return <Tag color={colors[ch] || "default"}>{ch}</Tag>;
      }
    },
    {
      title: "Priority",
      dataIndex: "priority",
      key: "priority",
      width: 100,
      render: (p) => {
        const opt = PRIORITY_OPTIONS.find((o) => o.value === p);
        return <Tag color={opt?.color || "default"}>{opt?.label || p}</Tag>;
      }
    },
    {
      title: "Cooldown",
      dataIndex: "cooldownHours",
      key: "cooldown",
      width: 90,
      render: (h) => `${h}h`
    },
    {
      title: "Daily Cap",
      dataIndex: "dailyCap",
      key: "dailyCap",
      width: 80
    },
    {
      title: "Suppress",
      dataIndex: "suppressIfActive",
      key: "suppress",
      width: 80,
      render: (v) => (v ? <Tag color="warning">Yes</Tag> : "No")
    },
    {
      title: "Banner",
      dataIndex: "showOnMedsPage",
      key: "banner",
      width: 70,
      render: (v) => (v ? <Tag color="blue">Yes</Tag> : "-")
    },
    {
      title: "Enabled",
      dataIndex: "isEnabled",
      key: "enabled",
      width: 80,
      render: (v) => <Switch checked={v} size="small" disabled />
    },
    {
      title: "Actions",
      key: "actions",
      width: 100,
      render: (_, record) => (
        <Space>
          <Button icon={<EditOutlined />} size="small" onClick={() => handleEdit(record)} />
          <Button icon={<DeleteOutlined />} size="small" danger onClick={() => handleDelete(record)} />
        </Space>
      )
    }
  ];

  return (
    <PageContainer>
      <Breadcrumb>
        <Breadcrumb.Item>
          <Link to="/settings">Settings</Link>
        </Breadcrumb.Item>
        <Breadcrumb.Item>Nudge Rules</Breadcrumb.Item>
      </Breadcrumb>

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", margin: "16px 0" }}>
        <div>
          <Title level={3} style={{ margin: 0 }}>
            Nudge Rules
          </Title>
          <Text type="secondary">
            Configure event-driven nudges with governance rules. Events can be internal (PA changes, insurance updates)
            or external (Genentech API).
          </Text>
        </div>
        <Button type="primary" icon={<PlusOutlined />} onClick={handleOpenDrawer}>
          Add Rule
        </Button>
      </div>

      <div style={{ flex: 1, overflow: "auto" }}>
        <Table
          columns={columns}
          dataSource={rules}
          rowKey="id"
          loading={loading}
          pagination={false}
          scroll={{ x: 1400 }}
        />
      </div>

      {/* Patient nudge history lookup */}
      <div style={{ marginTop: 16, padding: 16, border: "1px solid #f0f0f0", borderRadius: 8 }}>
        <Text strong style={{ display: "block", marginBottom: 8 }}>
          Patient Nudge History
        </Text>
        <Space>
          <Input
            placeholder="Enter Patient ID"
            value={patientSearchId}
            onChange={(e) => setPatientSearchId(e.target.value)}
            onPressEnter={handlePatientSearch}
            style={{ width: 200 }}
          />
          <Button icon={<SearchOutlined />} onClick={handlePatientSearch}>
            Search
          </Button>
        </Space>
        {patientNudges && (
          <div style={{ marginTop: 12, maxHeight: 200, overflow: "auto" }}>
            {patientNudges.length === 0 ? (
              <Text type="secondary">No nudges found for this patient.</Text>
            ) : (
              patientNudges.map((nudge) => (
                <Descriptions key={nudge.id} bordered size="small" column={6} style={{ marginBottom: 8 }}>
                  <Descriptions.Item label="Rule">{nudge.nudgeRule?.name || nudge.nudgeRule}</Descriptions.Item>
                  <Descriptions.Item label="Event">{nudge.event?.eventType || nudge.event}</Descriptions.Item>
                  <Descriptions.Item label="Channel">{nudge.channel}</Descriptions.Item>
                  <Descriptions.Item label="Status">
                    <Tag
                      color={nudge.status === "sent" ? "green" : nudge.status === "suppressed" ? "orange" : "default"}
                    >
                      {nudge.status}
                    </Tag>
                  </Descriptions.Item>
                  <Descriptions.Item label="Suppressed By">{nudge.suppressedBy || "-"}</Descriptions.Item>
                  <Descriptions.Item label="Sent">
                    {nudge.sentAt ? new Date(nudge.sentAt).toLocaleString() : "-"}
                  </Descriptions.Item>
                </Descriptions>
              ))
            )}
          </div>
        )}
      </div>

      {/* Create/Edit Drawer */}
      <Drawer
        title={editingItem ? "Edit Nudge Rule" : "Add Nudge Rule"}
        width={560}
        open={drawerVisible}
        onClose={() => {
          setDrawerVisible(false);
          setEditingItem(null);
        }}
        destroyOnClose
        footer={
          <Space style={{ float: "right" }}>
            <Button onClick={() => setDrawerVisible(false)}>Cancel</Button>
            <Button type="primary" loading={saving} onClick={handleSave}>
              Save
            </Button>
          </Space>
        }
      >
        <Form form={form} layout="vertical">
          <Form.Item name="name" label="Rule Name" rules={[{ required: true }]}>
            <Input placeholder="e.g. PA Denied - Outreach" />
          </Form.Item>

          <Form.Item name="description" label="Description">
            <TextArea rows={2} placeholder="Optional description..." />
          </Form.Item>

          <Form.Item name="brandJourneyConfiguration" label="Brand" rules={[{ required: true }]}>
            <Select placeholder="Select brand">
              {brands.map((b) => (
                <Option key={b.id} value={b.id}>
                  {b.brandName}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="eventType"
            label="Trigger Event"
            rules={[{ required: true }]}
            extra={(() => {
              const sel = form.getFieldValue("eventType");
              const et = EVENT_TYPES.find((e) => e.value === sel);
              if (!et) return null;
              const statusMsg = {
                live: "✅ Auto-detected by the platform — no API call needed.",
                api: "🔌 Must be pushed via the external API by the partner.",
                planned: "🔜 Not yet implemented — can be pushed via API in the meantime."
              };
              return (
                <span style={{ fontSize: 12 }}>
                  {statusMsg[et.status]} {et.hint && <span style={{ color: "#8f8c86" }}>{et.hint}</span>}
                </span>
              );
            })()}
          >
            <Select
              placeholder="Select event type"
              showSearch
              optionFilterProp="label"
              onChange={() => form.setFieldsValue({})}
              options={EVENT_TYPES.map((et) => ({
                value: et.value,
                label: et.label
              }))}
            />
          </Form.Item>

          <Form.Item name="channel" label="Delivery Channel" initialValue="in_app">
            <Radio.Group>
              {CHANNEL_OPTIONS.map((ch) => (
                <Radio.Button key={ch.value} value={ch.value}>
                  {ch.label}
                </Radio.Button>
              ))}
            </Radio.Group>
          </Form.Item>

          <Form.Item
            name="messageTemplate"
            label="Message Template"
            rules={[{ required: true }]}
            extra={
              <Text type="secondary" style={{ fontSize: 12 }}>
                Tokens: <code>{"{name}"}</code> patient name · <code>{"{drug}"}</code> drug name ·{" "}
                <code>{"{link}"}</code> secure link · <code>{"{event_detail}"}</code> event info
              </Text>
            }
          >
            <TextArea rows={3} placeholder="Hi {name}, we noticed a change in your {drug} coverage..." />
          </Form.Item>

          {/* Governance section */}
          <Text strong style={{ display: "block", margin: "16px 0 8px" }}>
            <ThunderboltOutlined /> Governance
          </Text>

          <Form.Item name="priority" label="Priority" initialValue={3}>
            <Select>
              {PRIORITY_OPTIONS.map((p) => (
                <Option key={p.value} value={p.value}>
                  <Tag color={p.color}>{p.label}</Tag> — Priority {p.value}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <div style={{ display: "flex", gap: 16 }}>
            <Form.Item name="cooldownHours" label="Cooldown (hours)" initialValue={24} style={{ flex: 1 }}>
              <InputNumber min={1} max={720} style={{ width: "100%" }} />
            </Form.Item>
            <Form.Item name="dailyCap" label="Daily Cap" initialValue={5} style={{ flex: 1 }}>
              <InputNumber min={1} max={50} style={{ width: "100%" }} />
            </Form.Item>
          </div>

          <Form.Item
            name="suppressIfActive"
            label="Suppress if Higher Priority Active"
            valuePropName="checked"
            initialValue={false}
          >
            <Switch />
          </Form.Item>

          <Form.Item
            name="showOnMedsPage"
            label="Show as banner on Meds page"
            valuePropName="checked"
            initialValue={false}
            extra="When enabled, the nudge also appears as a dismissible banner on the patient's Meds home page"
          >
            <Switch />
          </Form.Item>

          <Form.Item name="isEnabled" label="Enabled" valuePropName="checked" initialValue={true}>
            <Switch />
          </Form.Item>

          {/* External API example */}
          <div style={{ marginTop: 16, padding: 16, background: "#f6f6f6", borderRadius: 8 }}>
            <Text strong style={{ display: "block", marginBottom: 8, fontSize: 13 }}>
              External API — Trigger this nudge via API
            </Text>
            <Text type="secondary" style={{ display: "block", marginBottom: 8, fontSize: 12 }}>
              Partners (e.g., Genentech) can trigger this nudge by sending a POST request:
            </Text>
            <Form.Item noStyle shouldUpdate={(prev, cur) => prev.eventType !== cur.eventType}>
              {() => {
                const eventType = form.getFieldValue("eventType") || "EVENT_TYPE";
                return (
                  <pre
                    style={{
                      background: "#1e1e1e",
                      color: "#d4d4d4",
                      padding: 12,
                      borderRadius: 6,
                      fontSize: 11,
                      lineHeight: 1.6,
                      overflowX: "auto",
                      whiteSpace: "pre-wrap",
                      wordBreak: "break-all",
                      margin: 0
                    }}
                  >
                    <span style={{ color: "#569cd6" }}>curl</span>
                    {" -X POST "}
                    <span style={{ color: "#ce9178" }}>{`{BASE_URL}/api/v1/webhooks/nudge-event`}</span>
                    {" \\\n  -H "}
                    <span style={{ color: "#ce9178" }}>{`"Content-Type: application/json"`}</span>
                    {" \\\n  -H "}
                    <span style={{ color: "#ce9178" }}>{`"Authorization: Bearer {API_KEY_BASE64}"`}</span>
                    {" \\\n  -d '"}
                    <span style={{ color: "#ce9178" }}>
                      {JSON.stringify(
                        {
                          patientId: 12345,
                          eventType,
                          payload: { detail: "Example payload data" },
                          externalEventId: "unique-id-for-idempotency"
                        },
                        null,
                        2
                      )}
                    </span>
                    {"'"}
                  </pre>
                );
              }}
            </Form.Item>
            <Text type="secondary" style={{ display: "block", marginTop: 8, fontSize: 11 }}>
              The API returns <code>200 OK</code> immediately. The nudge is processed asynchronously. Use{" "}
              <code>externalEventId</code> to prevent duplicate processing.
            </Text>
          </div>
        </Form>
      </Drawer>
    </PageContainer>
  );
};

export { NudgeRuleConfiguration };
